/**
 * CheckoutSuccessPage — Route /checkout/success (Paket B, premium-checkout-redesign)
 *
 * Design-Referenz: design/checkout-success.html. Beleg (Tarif, Laufzeit,
 * Zahlart, Betrag, gültig bis) + 3 Next-Step-Karten.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { trackPurchase } from '../../lib/analytics'
import { Check, Radio, Bell, Code, BarChart3 } from 'lucide-react'
import { useCryptoPrices } from '../../hooks/useCryptoPrices'
import { useSubscription } from '../subscription/useSubscription'
import { useSubscriptionPlans, displayPrice } from '../subscription/useSubscriptionPlans'
import { formatSubscriptionDate } from '../subscription/subscriptionUtils'
import type { DurationMonths } from '../subscription/subscriptionUtils'
import { findCatalogPlan } from '../premium/legacyPlanMap'
import { planQuotas } from '../premium/planQuotas'
import { formatMoney } from '../premium/planMath'
import './checkout.css'

type CheckoutSuccessPageProps = {
  planId?: string
  term?: DurationMonths
  method?: 'card' | 'hnt' | 'credit'
}

function quotaLabel(value: number | 'unlimited' | 'unknown'): string {
  if (value === 'unlimited') return '∞'
  if (value === 'unknown') return '–'
  return String(value)
}

export function CheckoutSuccessPage({ planId, term = 1, method = 'card' }: CheckoutSuccessPageProps) {
  const { t } = useTranslation('checkout')
  const { currency } = useCryptoPrices()
  const { plans, isLoading } = useSubscriptionPlans(currency)
  const { subscription } = useSubscription()
  const plan = findCatalogPlan(plans, planId)

  // Kaufabschluss an GA4 melden. Der Effekt muss VOR den frühen Returns
  // stehen (Hook-Reihenfolge) und hängt an `plan.id`, nicht am Plan-Objekt —
  // sonst feuert er bei jedem Refetch erneut.
  const purchasedPlanId = plan?.id
  const purchaseValue = plan ? displayPrice(plan, term) : 0

  // Code-Review-Fix: ein pauschales eslint-disable auf den Effekt unten
  // unterdrückte nicht nur die gewollte Ausnahme (`purchaseValue`), sondern
  // stillschweigend auch `currency` und `term` — die standen ebenfalls nicht
  // in den Abhängigkeiten, ohne dass das so beabsichtigt war. Diese Ref hält
  // die aktuellsten Werte; der Effekt selbst bleibt exakt an `purchasedPlanId`
  // gebunden (feuert einmal pro Kauf, kein Zweitversand bei Preis-/
  // Währungs-Refetch) und braucht keinen Lint-Bypass mehr.
  const latestPurchaseDetails = useRef({ purchaseValue, currency, term })
  // Nicht direkt im Funktionskörper zuweisen (verboten — Ref-Mutation waehrend
  // des Renderns ist nicht rein) — dieser dependency-lose Effekt aktualisiert
  // die Ref stattdessen nach jedem Commit, bevor der Effekt unten sie liest.
  useEffect(() => {
    latestPurchaseDetails.current = { purchaseValue, currency, term }
  })

  useEffect(() => {
    if (!purchasedPlanId) return
    const { purchaseValue, currency, term } = latestPurchaseDetails.current
    trackPurchase({
      planId: purchasedPlanId,
      value: purchaseValue,
      currency,
      termMonths: term,
    })
  }, [purchasedPlanId])

  if (isLoading) {
    return <div aria-busy="true" data-testid="checkout-success-loading" />
  }

  if (!plan) {
    return (
      <div style={{ maxWidth: 640, margin: '40px auto', textAlign: 'center' }} data-testid="checkout-success-unknown-plan">
        <p className="muted">{t('unknownPlan.text')}</p>
        <Link to="/plans" className="btn btn-primary">{t('unknownPlan.link')}</Link>
      </div>
    )
  }

  const quotas = planQuotas(plan)
  const total = displayPrice(plan, term)
  const paymentMethod = subscription?.payment_method
  const validUntil = subscription?.current_period_end
  const renews = method === 'card'

  const NEXT = [
    { icon: <Radio size={16} />, key: 'hotspots', href: '/hotspots' as const },
    { icon: <Bell size={16} />, key: 'alerts', href: '/settings' as const },
    plan.api_access
      ? { icon: <Code size={16} />, key: 'api', href: '/developer' as const }
      : { icon: <BarChart3 size={16} />, key: 'report', href: '/monthly-report' as const },
  ]

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }} data-testid="checkout-success-page">
      <div className="card card-aurora card-pad-lg" style={{ textAlign: 'center', marginBottom: 18 }}>
        <div className="ok-ring"><Check size={28} strokeWidth={2} /></div>
        <h1 style={{ marginBottom: 6 }}>{t('success.welcome', { plan: plan.name })}</h1>
        <p className="muted" style={{ maxWidth: 420, margin: '0 auto' }}>
          {t('success.subtitle', { plan: plan.name })}
        </p>
        <div className="row" style={{ justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <span className="pill pill-success"><span className="dot" /> {t('success.activeNow')}</span>
          <span className="pill pill-primary">{quotaLabel(quotas.mobile)} Mobile · {quotaLabel(quotas.iot)} IoT</span>
        </div>
      </div>

      <div className="card card-pad" style={{ marginBottom: 18 }}>
        <h4 style={{ marginBottom: 10 }}>{t('success.receipt.title')}</h4>
        <div className="rec-row"><span className="muted">{t('success.receipt.plan')}</span><span className="v">{plan.name}</span></div>
        <div className="rec-row"><span className="muted">{t('success.receipt.term')}</span><span className="v">{t('duration.month', { count: term })}</span></div>
        <div className="rec-row">
          <span className="muted">{t('success.receipt.method')}</span>
          <span className="v" data-testid="receipt-method">
            {method === 'hnt'
              ? t('success.receipt.methodHnt')
              : method === 'credit'
                ? t('success.receipt.methodCredit')
                : paymentMethod
                  ? `${paymentMethod.brand} •••• ${paymentMethod.last4}`
                  : '—'}
          </span>
        </div>
        <div className="rec-row">
          <span className="muted">{t('success.receipt.paid')}</span>
          <span className="v" data-testid="receipt-total">
            {method === 'hnt' ? t('success.receipt.paidHnt', { amount: formatMoney(total, plan.currency) }) : formatMoney(total, plan.currency)}
          </span>
        </div>
        <div className="rec-row">
          <span className="muted">{t('success.receipt.validUntil')}</span>
          <span className="v" data-testid="receipt-valid-until">
            {validUntil ? formatSubscriptionDate(validUntil) : '—'}{' '}
            {renews ? t('success.receipt.validUntilRenews') : t('success.receipt.validUntilEnds')}
          </span>
        </div>
        <div className="row" style={{ gap: 8, marginTop: 14 }}>
          <a href="/payments" className="btn btn-sm">{t('success.downloadInvoice')}</a>
          <Link to="/account/subscription" className="btn btn-sm btn-ghost">{t('success.manageSubscription')}</Link>
        </div>
      </div>

      <div className="grid grid-3">
        {NEXT.map((n) => (
          <Link key={n.key} to={n.href} className="card card-pad is-hover next-card">
            <span className="nic">{n.icon}</span>
            <div>
              <b style={{ fontSize: 13 }}>{t(`success.next.${n.key}.title`)}</b>
              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{t(`success.next.${n.key}.sub`)}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
